import React, { useState } from "react";
import { BarChart3, ScrollText, BookUser, Search, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { panel } from "../../api/modulos.service";
import { coincide, coincidePlaca, normalizar } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, useCarga } from "./base";

/**
 * PANEL GENERAL, ESTADÍSTICAS, BITÁCORA Y DIRECTORIO.
 *
 * Cuatro pantallas de sólo lectura para la administración. Van en un archivo
 * porque comparten forma y datos: las cuatro son «mire lo que hay», y ninguna
 * escribe nada.
 */

const cifra = {
  padding: "var(--space-4)",
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border)",
  backgroundColor: "var(--bg-elevated)",
};
const numero = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-2xl)",
  fontWeight: 700,
  color: "var(--text)",
};
const etiqueta = { margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" };
const rejillaCifras = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
  gap: "var(--space-3)",
};

const Bloque = ({ titulo, datos, etiquetas }) => (
  <div style={{ display: "grid", gap: "var(--space-3)" }}>
    <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>{titulo}</h3>
    <div style={rejillaCifras}>
      {Object.entries(datos || {}).map(([clave, valor]) => (
        <div key={clave} style={cifra}>
          <p style={numero}>{valor}</p>
          <p style={etiqueta}>{etiquetas[clave] || clave}</p>
        </div>
      ))}
    </div>
  </div>
);

export function PanelGeneral() {
  const { datos, cargando, error, recargar } = useCarga(panel.general, []);
  /* El error se enseña, y corta ANTES del «Cargando…». Un panel que falla y
   * se queda cargando —o peor, en ceros— hace pensar que la residencial no
   * tuvo movimiento hoy. */
  if (error)
    return (
      <Aviso>
        {error}
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
            Reintentar
          </Button>
        </div>
      </Aviso>
    );
  if (cargando) return <Vacio>Cargando…</Vacio>;

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Panel general" subtitulo="Cómo está la residencial hoy." icono={BarChart3} />
      <Bloque titulo="Seguridad" datos={datos?.seguridad} etiquetas={{
        entradasHoy: "Entradas hoy", salidasHoy: "Salidas hoy", personasDentro: "Personas dentro",
        visitasPendientes: "Visitas pendientes", accesosRechazados: "Accesos rechazados",
        incidentes: "Incidentes abiertos",
      }} />
      <Bloque titulo="Residentes" datos={datos?.residentes} etiquetas={{
        activos: "Residentes activos", casasOcupadas: "Casas ocupadas", vehiculos: "Vehículos registrados",
      }} />
      <Bloque titulo="Visitantes" datos={datos?.visitantes} etiquetas={{
        hoy: "Hoy", semana: "Últimos 7 días", recurrentes: "Accesos recurrentes",
      }} />
      <Bloque titulo="Guardias" datos={datos?.guardias} etiquetas={{
        total: "Guardias", deTurno: "De turno ahora", turnosHoy: "Turnos hoy",
      }} />
      <Bloque titulo="Administración" datos={datos?.administracion} etiquetas={{
        admins: "Administradores", paquetesPendientes: "Paquetes sin retirar",
        mantenimientoAbierto: "Mantenimiento abierto", reservasPendientes: "Reservas por aprobar",
      }} />
    </div>
  );
}

export function Estadisticas() {
  const { datos, cargando, error, recargar } = useCarga(() => panel.estadisticas({}), []);
  if (error)
    return (
      <Aviso>
        {error}
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
            Reintentar
          </Button>
        </div>
      </Aviso>
    );
  if (cargando) return <Vacio>Cargando…</Vacio>;

  /* Barras dibujadas con `div`s y no con una librería de gráficas: son cinco
     listas ordenadas, y traer una dependencia entera para pintar rectángulos
     proporcionales pesa más de lo que aporta. */
  const Barras = ({ titulo, filas, claveEtiqueta, claveValor }) => {
    const max = Math.max(1, ...filas.map((f) => f[claveValor]));
    return (
      <div style={{ display: "grid", gap: "var(--space-2)" }}>
        <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>{titulo}</h3>
        {filas.length === 0 ? (
          <Vacio>Sin datos en el período.</Vacio>
        ) : (
          filas.map((f) => (
            <div key={String(f[claveEtiqueta])} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 160px) 1fr auto", gap: "var(--space-3)", alignItems: "center" }}>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{f[claveEtiqueta]}</span>
              <span style={{ height: "10px", borderRadius: "999px", backgroundColor: "var(--bg-tertiary)", overflow: "hidden" }}>
                <span style={{ display: "block", height: "100%", width: `${(f[claveValor] / max) * 100}%`, backgroundColor: "var(--gold)" }} />
              </span>
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text)" }}>{f[claveValor]}</span>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Estadísticas" subtitulo="Los últimos 30 días." icono={BarChart3} />
      <Barras titulo="Horas con más tráfico" filas={(datos?.porHora || []).map((h) => ({ ...h, hora: `${String(h.hora).padStart(2, "0")}:00` }))} claveEtiqueta="hora" claveValor="visitas" />
      <Barras titulo="Días con más visitas" filas={datos?.porDia || []} claveEtiqueta="dia" claveValor="visitas" />
      <Barras titulo="Por tipo de visitante" filas={datos?.porTipo || []} claveEtiqueta="tipo" claveValor="visitas" />
      <Barras titulo="Casas con más visitantes" filas={(datos?.porCasa || []).map((c) => ({ ...c, casa: `${c.poligono}-${c.casa}` }))} claveEtiqueta="casa" claveValor="visitas" />
      <Barras titulo="Por punto de entrada" filas={datos?.porPunto || []} claveEtiqueta="punto" claveValor="visitas" />
      <Bloque titulo="Otros" datos={{ accesosRechazados: datos?.accesosRechazados ?? 0 }} etiquetas={{ accesosRechazados: "Accesos rechazados" }} />
    </div>
  );
}

export function Bitacora() {
  const [ambito, setAmbito] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const { datos, cargando, error, recargar } = useCarga(() => panel.bitacora({ ambito: ambito || undefined, buscar: busqueda || undefined }), [ambito, busqueda]);

  const hora = (f) =>
    new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "short", timeStyle: "short" });

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Bitácora" subtitulo="Todo lo que pasó, quién lo hizo y cuándo. No se puede editar." icono={ScrollText} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
        <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar en la bitácora…" />
        <select style={{ ...est.control, maxWidth: "220px" }} value={ambito} onChange={(e) => setAmbito(e.target.value)}>
          <option value="">Todo</option>
          <option value="seguridad">Seguridad</option>
          <option value="administracion">Administración</option>
          <option value="residente">Residentes</option>
        </select>
      </div>

      {/* El error ocupa el lugar de la lista, no se suma a ella: «No hay
          movimientos.» y «no se pudo cargar» no pueden verse a la vez. */}
      {error ? (
        <Aviso>
          {error}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? <Vacio>Cargando…</Vacio> : (datos?.logs || []).length === 0 ? (
        <Vacio>No hay movimientos.</Vacio>
      ) : (
        <div>
          {datos.logs.map((l) => (
            <div key={l._id} style={{ display: "flex", gap: "var(--space-3)", padding: "var(--space-3) 0", borderBottom: "1px solid var(--border-light)", fontSize: "var(--text-sm)" }}>
              <span style={{ flexShrink: 0, color: "var(--text-tertiary)", fontVariantNumeric: "tabular-nums" }}>{hora(l.createdAt)}</span>
              <span style={{ color: "var(--text)" }}>
                {l.summary}
                {l.accessPoint?.name && <span style={{ color: "var(--text-tertiary)" }}> · {l.accessPoint.name}</span>}
              </span>
            </div>
          ))}
          <p style={{ ...est.subtitulo, marginTop: "var(--space-4)" }}>{datos.total} movimientos.</p>
        </div>
      )}
    </div>
  );
}

export function Directorio() {
  const [busqueda, setBusqueda] = useState("");
  const { datos, cargando, error, recargar } = useCarga(panel.directorio, []);

  // Sin tildes y sin espacios de sobra, con el ayudante compartido de
  // `utils/texto.js` — el mismo que usa el resto de los filtros.
  const coincideCon = (texto) => coincide(texto, busqueda);

  // Los filtros van aparte de la pintura porque hacen falta DOS veces: para
  // decidir qué grupo se pinta, y para saber si NINGUNO tiene filas — que es
  // cuando hay que decirlo, en vez de dejar el área en blanco.
  const filtros = {
    residentes: (r) => coincideCon(r.name) || coincideCon(r.houseNumber) || coincideCon(r.email),
    guardias: (g) => coincideCon(g.name),
    administradores: (a) => coincideCon(a.name) || coincideCon(a.email),
    // La placa se compara COMO PLACA: se guarda sin guiones.
    vehiculos: (v) =>
      coincidePlaca(v.plate, busqueda) || coincideCon(v.ownerName) || coincideCon(v.houseNumber),
  };

  const sinCoincidencias =
    normalizar(busqueda) !== "" &&
    Object.entries(filtros).every(([grupo, filtro]) => (datos?.[grupo] || []).filter(filtro).length === 0);

  const Grupo = ({ titulo, filas, pintar }) => {
    const visibles = filas.filter(pintar.filtro);
    if (visibles.length === 0) return null;
    return (
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>{titulo} ({visibles.length})</h3>
        <div style={est.rejilla}>
          {visibles.map((f) => (
            <div key={f._id} style={est.tarjeta}>{pintar.contenido(f)}</div>
          ))}
        </div>
      </div>
    );
  };

  if (error)
    return (
      <Aviso>
        {error}
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
            Reintentar
          </Button>
        </div>
      </Aviso>
    );
  if (cargando) return <Vacio>Cargando…</Vacio>;

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Directorio" subtitulo="Residentes, guardias, administración y vehículos." icono={BookUser} />
      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar en todo el directorio…" />

      <Grupo titulo="Residentes" filas={datos?.residentes || []} pintar={{
        filtro: filtros.residentes,
        contenido: (r) => (<>
          <h3 style={est.tarjetaTitulo}>{r.name}</h3>
          <div style={est.tarjetaDato}>Casa {r.polygon}-{r.houseNumber}</div>
          <div style={est.tarjetaDato}>{r.email}</div>
          {r.phone && <div style={est.tarjetaDato}>{r.phone}</div>}
        </>),
      }} />

      <Grupo titulo="Guardias" filas={datos?.guardias || []} pintar={{
        filtro: filtros.guardias,
        contenido: (g) => (<>
          <h3 style={est.tarjetaTitulo}>{g.name}</h3>
          <div style={{ ...est.tarjetaDato, color: g.onDuty ? "var(--success)" : "var(--text-tertiary)" }}>
            {g.onDuty ? "De turno ahora" : "Fuera de turno"}
          </div>
          {g.defaultAccessPoint?.name && <div style={est.tarjetaDato}>{g.defaultAccessPoint.name}</div>}
          {g.phone && <div style={est.tarjetaDato}>{g.phone}</div>}
        </>),
      }} />

      <Grupo titulo="Administración" filas={datos?.administradores || []} pintar={{
        filtro: filtros.administradores,
        contenido: (a) => (<>
          <h3 style={est.tarjetaTitulo}>{a.name}</h3>
          <div style={est.tarjetaDato}>{a.email}</div>
        </>),
      }} />

      <Grupo titulo="Vehículos" filas={datos?.vehiculos || []} pintar={{
        filtro: filtros.vehiculos,
        contenido: (v) => (<>
          <h3 style={{ ...est.tarjetaTitulo, letterSpacing: "0.06em" }}>{v.plate}</h3>
          <div style={est.tarjetaDato}>{[v.brand, v.model, v.color].filter(Boolean).join(" · ")}</div>
          <div style={est.tarjetaDato}>Casa {v.polygon}-{v.houseNumber}</div>
        </>),
      }} />

      {/* `Grupo` devuelve null cuando no hay filas, así que sin esto el área
          quedaba en blanco con sólo el ícono de la lupa. */}
      {sinCoincidencias && <Vacio>Nada coincide con «{busqueda}».</Vacio>}
    </div>
  );
}
